import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  CheckCircle2, Circle, Clock, AlertCircle, Edit3,
  FileText, Users, Phone, Calculator, DollarSign, Handshake, 
  FileCheck, Archive, ArrowRight, ArrowLeft 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { getStageDisplayName, getAllStagesStatus } from '../../api/claims';
import {
  ClaimDefinitionModal,
  TeamDefinitionModal,
  CustomerContactModal,
  SimpleStageModal
} from './workflow-modals';
import CalculationReviewModal from './workflow-modals/CalculationReviewModal';

const WORKFLOW_STAGES = [
  { 
    key: 'claim_definition', 
    label: 'Claim Definition', 
    icon: FileText,
    description: 'Define claim details and scope',
    checklist: ['Volume delta identified', 'OEM and part confirmed', 'Financial impact calculated']
  },
  { 
    key: 'team_definition', 
    label: 'Team Definition', 
    icon: Users,
    description: 'Assign team members and roles',
    checklist: ['Claim owner assigned', 'Watchers added', 'Responsibilities defined']
  },
  { 
    key: 'customer_contact', 
    label: 'Customer Contact', 
    icon: Phone,
    description: 'Initial communication with OEM',
    checklist: ['OEM contacted', 'Claim acknowledged', 'Contact person identified']
  },
  { 
    key: 'calculation', 
    label: 'Calculation', 
    icon: Calculator,
    description: 'Detailed cost and impact analysis',
    checklist: ['Costs calculated', 'Compensation estimated', 'Supporting data gathered']
  },
  { 
    key: 'cost_price_profit', 
    label: 'Cost | Price | Profit', 
    icon: DollarSign,
    description: 'Margin and profitability review',
    checklist: ['Action margin reviewed', 'Contribution margin calculated', 'Profitability confirmed']
  },
  { 
    key: 'quote_negotiation', 
    label: 'Quote & Negotiation', 
    icon: Handshake,
    description: 'Negotiation with OEM',
    checklist: ['Quote sent', 'Negotiation rounds tracked', 'Agreement terms defined']
  },
  { 
    key: 'settlement_invoicing', 
    label: 'Settlement & Invoicing', 
    icon: FileCheck,
    description: 'Final settlement and payment',
    checklist: ['Settlement agreed', 'Invoice sent', 'Payment terms confirmed']
  },
  { 
    key: 'closed', 
    label: 'Closed', 
    icon: Archive,
    description: 'Claim completed',
    checklist: ['Payment received', 'Documentation archived', 'Lessons learned recorded']
  }
];

const WorkflowManager = ({ claimId, currentStage, onStageChange }) => {
  const queryClient = useQueryClient();
  const [showAdvanceDialog, setShowAdvanceDialog] = useState(false);
  const [showBackDialog, setShowBackDialog] = useState(false);
  const [targetStage, setTargetStage] = useState(null);
  const [notes, setNotes] = useState('');
  const [activeModal, setActiveModal] = useState(null);

  // Fetch all stages status for completion indicators
  const { data: stagesStatusData } = useQuery({
    queryKey: ['allStagesStatus', claimId],
    queryFn: () => getAllStagesStatus(claimId),
    enabled: !!claimId
  });

  const stagesStatus = stagesStatusData?.data || [];
  
  // Get completion status for a stage
  const getStageStatus = (stageName) => {
    const status = stagesStatus.find(s => s.stage_name === stageName);
    return status || { is_completed: false, has_data: false };
  };

  // Determine the actual current stage based on completion status
  // Current stage is the first incomplete stage in the workflow
  const determineCurrentStage = () => {
    for (let i = 0; i < WORKFLOW_STAGES.length; i++) {
      const stage = WORKFLOW_STAGES[i];
      const status = getStageStatus(stage.key);
      if (!status.is_completed) {
        return i;
      }
    }
    // If all stages are completed, current is the last stage
    return WORKFLOW_STAGES.length - 1;
  };

  const actualCurrentIndex = stagesStatus.length > 0 ? determineCurrentStage() : 0;
  const currentIndex = actualCurrentIndex;
  const currentStageInfo = WORKFLOW_STAGES[currentIndex];
  const nextStage = currentIndex < WORKFLOW_STAGES.length - 1 ? WORKFLOW_STAGES[currentIndex + 1] : null;
  const prevStage = currentIndex > 0 ? WORKFLOW_STAGES[currentIndex - 1] : null;

  // Calculate days in current stage from stage data
  const calculateDaysInStage = () => {
    const currentStageStatus = getStageStatus(currentStageInfo.key);
    
    // If current stage has data, calculate from when it was last updated
    // Otherwise, calculate from when previous stage was completed
    let startDate = null;
    
    if (currentIndex > 0) {
      // Get the previous completed stage's completion date
      const prevStageStatus = getStageStatus(WORKFLOW_STAGES[currentIndex - 1].key);
      if (prevStageStatus.completed_at) {
        startDate = new Date(prevStageStatus.completed_at);
      }
    }
    
    // If no previous completion date, use current stage created date (fallback)
    if (!startDate && stagesStatus.length > 0) {
      const currentData = stagesStatus.find(s => s.stage_name === currentStageInfo.key);
      if (currentData?.created_at) {
        startDate = new Date(currentData.created_at);
      }
    }
    
    if (!startDate) return 0;
    
    const now = new Date();
    const diffTime = Math.abs(now - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysInStage = calculateDaysInStage();
  const isOverdue = daysInStage > 30;

  const handleAdvanceClick = (stage) => {
    setTargetStage(stage);
    setShowAdvanceDialog(true);
  };

  const handleBackClick = (stage) => {
    setTargetStage(stage);
    setShowBackDialog(true);
  };

  const handleStageClick = (stage) => {
    setActiveModal(stage.key);
  };

  const handleModalClose = () => {
    setActiveModal(null);
    queryClient.invalidateQueries(['allStagesStatus', claimId]);
    queryClient.invalidateQueries(['claimDetail', claimId]);
  };

  const handleConfirmAdvance = async () => {
    try {
      // Complete the current stage to advance
      const { completeStage } = await import('../../api/claims');
      await completeStage(claimId, currentStageInfo.key, notes || `Advanced to ${targetStage.label}`);
      
      // Refresh data
      queryClient.invalidateQueries(['allStagesStatus', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      
      setShowAdvanceDialog(false);
      setNotes('');
    } catch (error) {
      console.error('Failed to advance stage:', error);
      alert('Failed to advance stage. Please try again.');
    }
  };

  const handleConfirmBack = async () => {
    try {
      // Reopen the current stage to go back
      const { reopenStage } = await import('../../api/claims');
      
      // First, reopen current stage
      await reopenStage(claimId, currentStageInfo.key, notes || `Moved back to ${targetStage.label}`);
      
      // Then reopen the target stage if needed
      if (targetStage.key !== currentStageInfo.key) {
        await reopenStage(claimId, targetStage.key, notes || 'Reopening stage');
      }
      
      // Refresh data
      queryClient.invalidateQueries(['allStagesStatus', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      
      setShowBackDialog(false);
      setNotes('');
    } catch (error) {
      console.error('Failed to go back:', error);
      alert('Failed to go back. Please try again.');
    }
  };

  const StageIcon = currentStageInfo?.icon || Circle;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg">Workflow Manager</CardTitle>
              <Badge variant="outline" className="font-normal">
                Stage {currentIndex + 1} of {WORKFLOW_STAGES.length}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {isOverdue && (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Overdue
                </Badge>
              )}
              <Badge variant="secondary" className="gap-1">
                <Clock className="h-3 w-3" />
                {daysInStage} days in stage
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Stage Info */}
          <div className="flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="p-2 bg-blue-600 rounded-lg">
              <StageIcon className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-blue-900">{currentStageInfo?.label}</h3>
                <Badge variant="default">Current</Badge>
              </div>
              <p className="text-sm text-blue-700 mb-3">{currentStageInfo?.description}</p>
              
              {/* Stage Checklist */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-blue-900">Stage Checklist:</p>
                {currentStageInfo?.checklist.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-blue-800">
                    <Circle className="h-3 w-3" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Stage Navigation Buttons */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!prevStage}
              onClick={() => handleBackClick(prevStage)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to {prevStage?.label}
            </Button>
            
            <Button
              variant="default"
              size="sm"
              disabled={!nextStage}
              onClick={() => handleAdvanceClick(nextStage)}
              className="gap-2"
            >
              Advance to {nextStage?.label}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {/* All Stages Timeline */}
          <div className="pt-4 border-t">
            <div className="space-y-2">
              {WORKFLOW_STAGES.map((stage, index) => {
                const stageStatus = getStageStatus(stage.key);
                const isCurrent = index === currentIndex;
                const Icon = stage.icon;

                return (
                  <div
                    key={stage.key}
                    onClick={() => handleStageClick(stage)}
                    className={`flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer hover:bg-gray-100 ${
                      isCurrent ? 'bg-blue-50 hover:bg-blue-100' : stageStatus.is_completed ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {stageStatus.is_completed ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : isCurrent ? (
                          <Clock className="h-5 w-5 text-blue-600" />
                        ) : stageStatus.has_data ? (
                          <Edit3 className="h-5 w-5 text-orange-500" />
                        ) : (
                          <Circle className="h-5 w-5 text-gray-400" />
                        )}
                      </div>

                      {/* Stage Icon & Label */}
                      <Icon className={`h-4 w-4 ${
                        stageStatus.is_completed ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-gray-400'
                      }`} />
                      
                      <span className={`text-sm font-medium ${
                        isCurrent ? 'text-blue-900' : stageStatus.is_completed ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        {stage.label}
                      </span>
                    </div>

                    {/* Status Badge */}
                    <div className="flex items-center gap-2">
                      {stageStatus.is_completed && (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                          Complete
                        </Badge>
                      )}
                      {!stageStatus.is_completed && stageStatus.has_data && (
                        <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">
                          Draft
                        </Badge>
                      )}
                      <span className="text-xs text-gray-500">{index + 1}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advance Stage Dialog */}
      <Dialog open={showAdvanceDialog} onOpenChange={setShowAdvanceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Advance to {targetStage?.label}?</DialogTitle>
            <DialogDescription>
              This will move the claim to the next stage. You can optionally add notes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Add notes (optional)..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdvanceDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmAdvance}>
              Advance Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Back Dialog */}
      <Dialog open={showBackDialog} onOpenChange={setShowBackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Back to {targetStage?.label}?</DialogTitle>
            <DialogDescription className="text-orange-600">
              Moving backwards requires a reason. Please explain why.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Reason for moving back (required)..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              required
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmBack}>
              Move Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Modals */}
      <ClaimDefinitionModal
        open={activeModal === 'claim_definition'}
        onOpenChange={(open) => !open && handleModalClose()}
        claimId={claimId}
        onComplete={handleModalClose}
      />

      <TeamDefinitionModal
        open={activeModal === 'team_definition'}
        onOpenChange={(open) => !open && handleModalClose()}
        claimId={claimId}
        onComplete={handleModalClose}
      />

      <CustomerContactModal
        open={activeModal === 'customer_contact'}
        onOpenChange={(open) => !open && handleModalClose()}
        claimId={claimId}
        onComplete={handleModalClose}
      />

      <CalculationReviewModal
        open={activeModal === 'calculation'}
        onOpenChange={(open) => !open && handleModalClose()}
        claimId={claimId}
        onComplete={handleModalClose}
      />

      <SimpleStageModal
        open={activeModal === 'cost_price_profit'}
        onOpenChange={(open) => !open && handleModalClose()}
        claimId={claimId}
        stageName="cost_price_profit"
        stageTitle="Cost | Price | Profit Review"
        stageDescription="Final review and approval of financial calculations and margins."
        notesLabel="Management Review & Approval Notes"
        notesPlaceholder="Document management approval, final margin review, and any adjustments to the financial calculation..."
        showDocuments={false}
        onComplete={handleModalClose}
      />

      <SimpleStageModal
        open={activeModal === 'quote_negotiation'}
        onOpenChange={(open) => !open && handleModalClose()}
        claimId={claimId}
        stageName="quote_negotiation"
        stageTitle="Quote & Negotiation"
        stageDescription="Track negotiation rounds, quotes sent, and agreement terms with the OEM."
        notesLabel="Negotiation Log"
        notesPlaceholder="Log negotiation rounds, quotes sent, counter-offers, and agreement terms..."
        showDocuments={true}
        onComplete={handleModalClose}
      />

      <SimpleStageModal
        open={activeModal === 'settlement_invoicing'}
        onOpenChange={(open) => !open && handleModalClose()}
        claimId={claimId}
        stageName="settlement_invoicing"
        stageTitle="Settlement & Invoicing"
        stageDescription="Finalize settlement amount, send invoices, and confirm payment terms."
        notesLabel="Settlement Details"
        notesPlaceholder="Document settlement amount, invoice details, payment terms, and expected payment date..."
        showDocuments={true}
        onComplete={handleModalClose}
      />

      <SimpleStageModal
        open={activeModal === 'closed'}
        onOpenChange={(open) => !open && handleModalClose()}
        claimId={claimId}
        stageName="closed"
        stageTitle="Closed"
        stageDescription="Complete the claim with final summary and lessons learned."
        notesLabel="Closure Summary"
        notesPlaceholder="Document final outcome, payment received, lessons learned, and any follow-up actions..."
        showDocuments={false}
        onComplete={handleModalClose}
      />
    </>
  );
};

export default WorkflowManager;
